import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import type React from 'react';

export function SwitchGroup({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      data-slot="control"
      {...props}
      className={clsx(
        className,
        // Basic groups
        'space-y-3 **:data-[slot=label]:font-normal',
        // With descriptions
        'has-data-[slot=description]:space-y-6 has-data-[slot=description]:**:data-[slot=label]:font-medium'
      )}
    />
  );
}

export function SwitchField({
  className,
  ...props
}: { className?: string } & Omit<Headless.FieldProps, 'as' | 'className'>) {
  return (
    <Headless.Field
      data-slot="field"
      {...props}
      className={clsx(
        className,
        // Base layout
        'grid grid-cols-[1fr_auto] gap-x-8 gap-y-1 sm:grid-cols-[1fr_auto]',
        // Control layout
        '*:data-[slot=control]:col-start-2 *:data-[slot=control]:self-start sm:*:data-[slot=control]:mt-0.5',
        // Label layout
        '*:data-[slot=label]:col-start-1 *:data-[slot=label]:row-start-1',
        // Description layout
        '*:data-[slot=description]:col-start-1 *:data-[slot=description]:row-start-2',
        // With description
        'has-data-[slot=description]:**:data-[slot=label]:font-medium'
      )}
    />
  );
}

const colors = {
  // Structural variants (grayscale)
  'dark/white': [
    '[--switch-bg-ring:var(--color-ds-gray-1000)]/90 [--switch-bg:var(--color-ds-gray-1000)] dark:[--switch-bg-ring:transparent] dark:[--switch-bg:var(--color-white)]',
    '[--switch-ring:var(--color-ds-gray-1000)]/90 [--switch-shadow:var(--color-black)]/10 [--switch:white] dark:[--switch-ring:transparent] dark:[--switch:var(--color-ds-gray-100)]',
  ],
  dark: [
    '[--switch-bg-ring:var(--color-ds-gray-1000)]/90 [--switch-bg:var(--color-ds-gray-1000)] dark:[--switch-bg-ring:var(--color-white)]/15',
    '[--switch-ring:var(--color-ds-gray-1000)]/90 [--switch-shadow:var(--color-black)]/10 [--switch:white] dark:[--switch:var(--color-ds-gray-100)]',
  ],
  zinc: [
    '[--switch-bg-ring:var(--color-ds-gray-800)]/90 [--switch-bg:var(--color-ds-gray-700)] dark:[--switch-bg-ring:transparent]',
    '[--switch-shadow:var(--color-black)]/10 [--switch:white] [--switch-ring:var(--color-ds-gray-800)]/90',
  ],
  white: [
    '[--switch-bg-ring:var(--color-black)]/15 [--switch-bg:white] dark:[--switch-bg-ring:transparent]',
    '[--switch-shadow:var(--color-black)]/10 [--switch-ring:transparent] [--switch:var(--color-ds-gray-1000)] dark:[--switch:var(--color-ds-gray-100)]',
  ],
  // Design system chromatic colors
  primary: [
    '[--switch-bg-ring:var(--color-ds-emerald-800)]/90 [--switch-bg:var(--color-ds-emerald-700)] dark:[--switch-bg-ring:transparent] dark:[--switch-bg:var(--color-ds-emerald-500)]',
    '[--switch:white] [--switch-ring:var(--color-ds-emerald-800)]/90 [--switch-shadow:var(--color-ds-emerald-900)]/20',
  ],
  red: [
    '[--switch-bg-ring:var(--color-ds-red-800)]/90 [--switch-bg:var(--color-ds-red-700)] dark:[--switch-bg-ring:transparent]',
    '[--switch:white] [--switch-ring:var(--color-ds-red-800)]/90 [--switch-shadow:var(--color-ds-red-900)]/20',
  ],
  blue: [
    '[--switch-bg-ring:var(--color-ds-blue-800)]/90 [--switch-bg:var(--color-ds-blue-700)] dark:[--switch-bg-ring:transparent]',
    '[--switch:white] [--switch-ring:var(--color-ds-blue-800)]/90 [--switch-shadow:var(--color-ds-blue-900)]/20',
  ],
  green: [
    '[--switch-bg-ring:var(--color-ds-green-800)]/90 [--switch-bg:var(--color-ds-green-700)] dark:[--switch-bg-ring:transparent]',
    '[--switch:white] [--switch-ring:var(--color-ds-green-800)]/90 [--switch-shadow:var(--color-ds-green-900)]/20',
  ],
  amber: [
    '[--switch-bg-ring:var(--color-ds-amber-700)]/80 [--switch-bg:var(--color-ds-amber-600)] dark:[--switch-bg-ring:transparent]',
    '[--switch-ring:transparent] [--switch-shadow:transparent] [--switch:var(--color-ds-amber-1000)]',
  ],
  teal: [
    '[--switch-bg-ring:var(--color-ds-teal-800)]/90 [--switch-bg:var(--color-ds-teal-700)] dark:[--switch-bg-ring:transparent]',
    '[--switch:white] [--switch-ring:var(--color-ds-teal-800)]/90 [--switch-shadow:var(--color-ds-teal-900)]/20',
  ],
  purple: [
    '[--switch-bg-ring:var(--color-ds-purple-800)]/90 [--switch-bg:var(--color-ds-purple-700)] dark:[--switch-bg-ring:transparent]',
    '[--switch:white] [--switch-ring:var(--color-ds-purple-800)]/90 [--switch-shadow:var(--color-ds-purple-900)]/20',
  ],
  pink: [
    '[--switch-bg-ring:var(--color-ds-pink-800)]/90 [--switch-bg:var(--color-ds-pink-700)] dark:[--switch-bg-ring:transparent]',
    '[--switch:white] [--switch-ring:var(--color-ds-pink-800)]/90 [--switch-shadow:var(--color-ds-pink-900)]/20',
  ],
};

type Color = keyof typeof colors;

export function Switch({
  color = 'dark/white',
  className,
  ...props
}: {
  color?: Color;
  className?: string;
} & Omit<Headless.SwitchProps, 'as' | 'className' | 'children'>) {
  return (
    <Headless.Switch
      data-slot="control"
      {...props}
      className={clsx(
        className,
        // Base styles
        'group relative isolate inline-flex h-6 w-10 cursor-pointer rounded-full p-[3px] sm:h-5 sm:w-8',
        // Transitions
        'transition duration-0 ease-in-out data-changing:duration-200',
        // Outline and background color in forced-colors mode so switch is still visible
        'forced-colors:outline forced-colors:[--switch-bg:Highlight] dark:forced-colors:[--switch-bg:Highlight]',
        // Unchecked
        'bg-muted ring-1 ring-border dark:ring-ds-gray-500 ring-inset',
        // Checked
        'data-checked:bg-(--switch-bg) data-checked:ring-(--switch-bg-ring) dark:data-checked:bg-(--switch-bg) dark:data-checked:ring-(--switch-bg-ring)',
        // Focus
        'focus:not-data-focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-primary',
        // Hover
        'data-hover:ring-border data-hover:data-checked:ring-(--switch-bg-ring)',
        // Disabled
        'data-disabled:bg-muted data-disabled:opacity-50 data-disabled:data-checked:bg-muted data-disabled:data-checked:ring-border',
        // Color specific styles
        colors[color]
      )}
    >
      <span
        aria-hidden="true"
        className={clsx(
          // Basic layout
          'pointer-events-none relative inline-block size-4.5 rounded-full sm:size-3.5',
          // Transition
          'translate-x-0 transition duration-200 ease-in-out',
          // Invisible border so the switch is still visible in forced-colors mode
          'border border-transparent',
          // Unchecked
          'bg-white shadow-sm ring-1 ring-black/5',
          // Checked
          'group-data-checked:bg-(--switch) group-data-checked:shadow-(--switch-shadow) group-data-checked:ring-(--switch-ring)',
          'group-data-checked:translate-x-4 sm:group-data-checked:translate-x-3',
          // Disabled
          'group-data-checked:group-data-disabled:bg-white group-data-checked:group-data-disabled:shadow-sm group-data-checked:group-data-disabled:ring-black/5'
        )}
      />
    </Headless.Switch>
  );
}
